import {
  Box,
  CollapsibleCard,
  ConfirmationDialogBox,
  IconButton,
} from '@atgfr8/atg-ui-tailwind';
import { AutoComplete, ToastType } from '@components';
import {
  addAlternateCustomer,
  CustomerLookup,
  customerLTLSettings,
  getAlternateCustomers,
  getPotentialAlternateCustomerOptions,
  removeAlternateCustomer,
} from '@store/customers/customerDetails/LTL';
import { DeleteIcon, CheckIcon, AccountIcon } from '@atgfr8/icons';
import { handleToast } from '@utils/utils';
import { size } from 'lodash';
import { createEffect, createSignal, For, Show } from 'solid-js';

import { AlternateCustomer as classes } from './classes';

const AlternateCustomer = (props: { customerId: number }) => {
  const [alternateCustomerToRemove, setalternateCustomerToRemove] =
    createSignal<CustomerLookup | null>(null);
  const [alternateCustomerToAdd, setalternateCustomerToAdd] =
    createSignal<CustomerLookup | null>(null);
  const [getIsDeleteDialogOpen, setIsDeleteDialogOpen] = createSignal(false);
  const [getIsSaveDialogOpen, setIsSaveDialogOpen] = createSignal(false);

  createEffect(async () => {
    if (props.customerId) {
      await Promise.all([
        getAlternateCustomers(props.customerId),
        getPotentialAlternateCustomerOptions(props.customerId),
      ]);
    }
  });

  const alternateCustomers = () =>
    customerLTLSettings.cspSetup.scacAccountCustomer?.alternateCustomers;

  const customer = () => customerLTLSettings.cspSetup.scacAccountCustomer;

  const alternateCustomerOptions = () =>
    customerLTLSettings.cspSetup.alternateCustomerOptions;

  const handleDelete = async () => {
    if (alternateCustomerToRemove() != null) {
      await removeAlternateCustomer(alternateCustomerToRemove()!);
      setalternateCustomerToRemove(null);
      await getAlternateCustomers(props.customerId);
      handleToast(ToastType.Success, 'Alternate Customer Successfully Removed');
    }
  };

  const handleSave = async () => {
    if (alternateCustomerToAdd() != null) {
      await addAlternateCustomer(
        props.customerId,
        alternateCustomerToAdd()!.customerId,
      );
      setalternateCustomerToAdd(null);
      await getAlternateCustomers(props.customerId);
      handleToast(ToastType.Success, 'Alternate Customer Successfully Added');
    }
  };

  const onDelete = (customer: CustomerLookup) => {
    setalternateCustomerToRemove(customer);
    setIsDeleteDialogOpen(true);
  };

  const onSave = (alternateCustomer: CustomerLookup) => {
    setalternateCustomerToAdd(alternateCustomer);
    setIsSaveDialogOpen(true);
  };
  return (
    <CollapsibleCard
      startTitle="Alternate Customers"
      loading={customerLTLSettings.isLoading}
      class="mb-2"
    >
      <Show
        when={customer()?.isAlternateCustomer === false}
        fallback={
          <Box class={classes.noCustomerContainer}>
            <Box class={classes.noCustomerText}>
              This customer is registered as an alternate customer.
            </Box>
            <Box class="flex items-center justify-center mt-2">
              <Box class="font-medium text-base">Remove</Box>
              <IconButton
                onClick={() => {
                  const alternateCustomerChoice = customer();
                  if (alternateCustomerChoice)
                    onDelete({
                      customerId: alternateCustomerChoice.customerId,
                      name: alternateCustomerChoice.customerName,
                      status: 'Active',
                    });
                }}
                dataTestId="remove-current-alternate-customer-button"
              >
                <DeleteIcon class="text-[22px]! text-red-700" />
              </IconButton>
            </Box>
          </Box>
        }
      >
        <Box class={classes.container}>
          Add other customers you want to receive the same Customer Specific
          Pricing LTL quote rates. If you cannot find the customer, make sure it
          is active and has CSP Enabled.
        </Box>

        <AutoComplete
          id="autocomplete-csp-customer-options-search"
          endpoint=""
          isStatic={true}
          fetchData={(searchQuery, setResults) => {
            const allCustomers = alternateCustomerOptions();
            if (!searchQuery) {
              setResults(allCustomers);
              return;
            }
            const lower = searchQuery.toLowerCase();
            const filtered = allCustomers.filter((customer) =>
              customer.name.toLowerCase().includes(lower),
            );
            setResults(filtered);
          }}
          onItemSelect={(item) => onSave(item as CustomerLookup)}
          multipleValueRender={() => {
            return (
              <For each={alternateCustomers()}>
                {(item) => (
                  <Box class="flex mt-1 justify-between p-2 border-2 shadow-xs">
                    <Box class="flex gap-8 items-center">
                      <AccountIcon class="text-[#026EA1]" />
                      <Box class="font-medium text-base">
                        {item.customerName}
                      </Box>
                    </Box>
                    <Box class="flex flex-col items-center justify-center w-14">
                      <IconButton
                        onClick={() =>
                          onDelete({
                            customerId: item.customerId,
                            name: item.customerName,
                            status: 'Active',
                          })
                        }
                        dataTestId={`remove-alternate-customer-button-${item.customerId}`}
                      >
                        <DeleteIcon class="text-[22px]! text-red-700" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </For>
            );
          }}
          renderMultipleKey="customerName"
          mode="multiple"
          defaultValue=""
          label=""
          placeholder="Find a customer ..."
          version="v3"
          renderItem={(item) => {
            const customerOption = item as CustomerLookup;
            return (
              <Box class="flex gap-1">
                <AccountIcon class="text-[#026EA1]" />
                <Box class="font-medium text-base">{customerOption.name}</Box>
                <Box class="flex flex-col w-14">
                  <span class={classes.customerLTLBox}>
                    LTL
                    <CheckIcon class="text-[13px]! text-white" />
                  </span>
                </Box>
              </Box>
            );
          }}
        />
      </Show>
      <Show
        when={
          size(alternateCustomers()) === 0 &&
          customer() &&
          customer()?.isAlternateCustomer === false
        }
      >
        <Box class={classes.noCustomerContainer}>
          <Box class={classes.noCustomerText}>
            No alternate customers have been added yet.
          </Box>
        </Box>
      </Show>

      <ConfirmationDialogBox
        dataTestId="AlternateCustomerDeleteDialog"
        id="AlternateCustomerDeleteDialog"
        title="Are you sure you want to delete this shared Customer Specific Pricing?"
        onSubmit={handleDelete}
        onSubmitText="Delete"
        onCancel={() => setalternateCustomerToRemove(null)}
        isDialogOpen={getIsDeleteDialogOpen()}
        closeDialogBox={(_reason) => setIsDeleteDialogOpen(false)}
      />
      <ConfirmationDialogBox
        dataTestId="AlternateCustomerSaveDialog"
        id="AlternateCustomerSaveDialog"
        title="Are you sure you want to add this alternate customer and allow it to receive the same LTL pricing?"
        onSubmit={handleSave}
        onSubmitText="Confirm & Save"
        onCancel={() => setalternateCustomerToAdd(null)}
        isDialogOpen={getIsSaveDialogOpen()}
        closeDialogBox={(_reason) => setIsSaveDialogOpen(false)}
      />
    </CollapsibleCard>
  );
};

export default AlternateCustomer;
